import React, { useState, memo, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import '../styles/ExploreCauses.css';
import { FaSearch, FaHeart, FaGlobe, FaThLarge, FaMapMarkedAlt, FaPaw, FaBook, FaHandHoldingHeart, FaLeaf, FaPalette, FaHeartbeat, FaUsers, FaChild, FaMicroscope, FaBalanceScale, FaHandsHelping, FaWater, FaLaptopCode } from 'react-icons/fa';
import CauseDetail from '../components/CauseDetail';
import { categories } from '../data/categories';
import { causes } from '../data/causes';

const MapChart = memo(({ selectedState, onStateClick, onLoad }) => {
  React.useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  return (
    <ComposableMap
      projection="geoAlbersUsa"
      projectionConfig={{ 
        scale: 1000,
        center: [0, 40]
      }}
      style={{
        width: "100%",
        height: "100%"
      }}
    >
      <Geographies geography="/usa-states.json">
        {({ geographies }) =>
          geographies.map(geo => {
            const stateName = geo.properties.name;
            
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onClick={() => {
                  console.log('Clicking state:', stateName);
                  onStateClick(stateName);
                }}
                style={{
                  default: {
                    fill: stateName === selectedState ? "#059669" : "#1f2937",
                    stroke: "#ffffff",
                    strokeWidth: 0.5,
                    outline: "none",
                    cursor: 'pointer'
                  },
                  hover: {
                    fill: "#059669",
                    stroke: "#ffffff",
                    strokeWidth: 0.5,
                    outline: "none"
                  },
                  pressed: {
                    fill: "#059669",
                    stroke: "#ffffff",
                    strokeWidth: 0.5,
                    outline: "none"
                  }
                }}
              />
            );
          })
        }
      </Geographies>
    </ComposableMap>
  );
});

const ExploreCauses = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCause, setSelectedCause] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [geographies, setGeographies] = useState([]);

  // Load geographies when component mounts
  useEffect(() => {
    fetch('/usa-states.json')
      .then(response => response.json())
      .then(data => {
        const geos = data.objects.states.geometries;
        setGeographies(geos);
      })
      .catch(error => console.error('Error loading map data:', error));
  }, []);

  const getStateName = (stateCode) => {
    if (!stateCode) return '';
    const state = geographies.find(geo => 
      geo.properties.STATE_CODE === stateCode
    );
    return state?.properties.name || stateCode;
  };

  const handleCauseClick = (cause) => {
    setSelectedCause(cause);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseDetail = () => {
    setSelectedCause(null);
    document.body.style.overflow = 'auto';
  };

  const filteredCauses = causes.filter(cause => {
    const matchesCategory = selectedCategory === 'all' || 
                          (cause.categories && cause.categories.includes(selectedCategory));
    const matchesState = !selectedState || 
                        cause.state === selectedState || 
                        cause.stateTag === selectedState;
    const matchesSearch = searchTerm === '' || 
                         cause.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cause.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesState && matchesSearch;
  });

  return (
    <div className="explore-wrapper">
      <div className="terminology-banner">
        <div className="terminology-content">
          <h2>What is a Cause?</h2>
          <p>
            At Cause & Impact, we use the term "Cause" to refer to charitable projects 
            operating under our fiscal sponsorship. Each Cause represents a unique mission 
            to create positive change, backed by our nonprofit infrastructure and support.
          </p>
        </div>
      </div>

      <div className="explore-container">
        <div className="explore-header">
          <h1>Explore Our Causes</h1>
          <p>Discover and support innovative charitable initiatives making real impact</p>
          
          <div className="search-section">
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search Causes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="categories">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon}
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          <div className="view-toggle-container">
            <div className="view-toggle">
              <button 
                className={`view-toggle-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <FaThLarge /> Grid View
              </button>
              <button 
                className={`view-toggle-button ${viewMode === 'map' ? 'active' : ''}`}
                onClick={() => setViewMode('map')}
              >
                <FaMapMarkedAlt /> State View
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'map' && (
          <div className="map-section">
            <div className="map-view">
              <div className={`map-loading ${!isMapLoading ? 'hidden' : ''}`}>
                Loading map...
              </div>
              <MapChart 
                selectedState={selectedState}
                onStateClick={(state) => {
                  setSelectedState(state === selectedState ? null : state);
                }}
                onLoad={() => setIsMapLoading(false)}
              />
              <div className="map-legend">
                <div className="legend-item">
                  <span className="legend-color active"></span>
                  <span>States Selected</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color has-causes"></span>
                  <span>States with Causes</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color no-causes"></span>
                  <span>States without Causes</span>
                </div>
              </div>
            </div>

            {selectedState && (
              <div className="state-causes">
                <h2>Causes in {getStateName(selectedState)}</h2>
                {filteredCauses.length > 0 ? (
                  <div className="causes-grid">
                    {filteredCauses.map((cause) => (
                      <div
                        key={cause.id}
                        className="cause-card"
                        onClick={() => handleCauseClick(cause)}
                      >
                        <div className="cause-icon-wrapper">{cause.icon}</div>
                        <div className="cause-content">
                          <h3>{cause.name}</h3>
                          <p>{cause.shortDescription}</p>
                          <div className="cause-impact">
                            <FaHeart />
                            <span>${cause.fundsRaised.toLocaleString()} raised</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-causes-message">
                    <p>
                      Looks like there aren't any Causes in {getStateName(selectedState)} yet. 
                      <strong> Be the first to make an impact!</strong>
                    </p>
                    <a href="/apply" className="cta-button primary">Start a Cause</a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="featured-section">
          <h2>Featured Causes</h2>
          <div className="causes-grid">
            {causes
              .filter(cause => 
                (selectedCategory === 'all' || cause.categories?.includes(selectedCategory)) &&
                (!selectedState || cause.state === selectedState || cause.stateTag === selectedState) &&
                (searchTerm === '' || 
                  cause.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  cause.description.toLowerCase().includes(searchTerm.toLowerCase()))
              )
              .map((cause) => (
                <div 
                  key={cause.id} 
                  className="cause-card"
                  onClick={() => handleCauseClick(cause)}
                >
                  <div className="cause-icon-wrapper">
                    {cause.icon}
                  </div>
                  <div className="cause-content">
                    <h3>{cause.name}</h3>
                    <p>{cause.shortDescription}</p>
                    <div className="cause-impact">
                      <FaHeart />
                      <span>${cause.fundsRaised.toLocaleString()} raised</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {selectedCause && (
          <CauseDetail 
            cause={selectedCause} 
            onClose={handleCloseDetail}
          />
        )}

        <div className="start-cause-section">
          <div className="start-cause-content">
            <h2>Ready to Make Your Impact?</h2>
            <p>
              Have a charitable initiative you'd like to launch? Join our community of 
              change-makers and turn your vision into reality.
            </p>
            <div className="cta-buttons">
              <a href="/apply" className="cta-button primary">Start Your Cause</a>
              <a href="/learn-more" className="cta-button secondary">Learn More</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreCauses; 